export type SocialNetworkKey =
  | 'twitterId'
  | 'instagramId'
  | 'facebookId'
  | 'redditId'
  | 'pixivId'

export interface SocialNetworkMeta {
  key: SocialNetworkKey
  label: string
  domain: string
  usernameVerify: RegExp
  urlExtract: RegExp
  /** Example username for display when input is invalid */
  example: string
}

export const SOCIAL_NETWORKS: SocialNetworkMeta[] = [
  {
    key: 'twitterId',
    label: 'user:profile.twitter-username',
    domain: 'https://x.com/',
    usernameVerify: /^[a-zA-Z0-9_]{1,15}$/,
    urlExtract:
      /(?:twitter\.com|x\.com)\/@?(?<username>[a-zA-Z0-9_]{1,15})\/?$/,
    example: 'PixAI_Official',
  },
  {
    key: 'instagramId',
    label: 'user:profile.instagram-username',
    domain: 'https://www.instagram.com/',
    usernameVerify: /^[a-zA-Z0-9_.]{1,30}$/,
    urlExtract: /instagram\.com\/@?(?<username>[a-zA-Z0-9_.]{1,30})\/?$/,
    example: 'pixai_official',
  },
  {
    key: 'facebookId',
    label: 'user:profile.facebook-username',
    domain: 'https://www.facebook.com/',
    usernameVerify: /^(?:\d+|[a-zA-Z0-9.]{1,50})$/,
    urlExtract:
      /facebook\.com\/(?:profile\.php\?id=)?(?<username>[a-zA-Z0-9.]+)\/?$/,
    example: '100081244069311',
  },
  {
    key: 'redditId',
    label: 'user:profile.reddit-username',
    domain: 'https://www.reddit.com/user/',
    usernameVerify: /^[a-zA-Z0-9_-]{3,20}$/,
    urlExtract:
      /reddit\.com\/u(?:ser)?\/@?(?<username>[a-zA-Z0-9_-]{3,20})\/?$/,
    example: 'PixAI_Official',
  },
  {
    key: 'pixivId',
    label: 'user:profile.pixiv-userid',
    domain: 'https://www.pixiv.net/users/',
    usernameVerify: /^\d+$/,
    urlExtract: /pixiv\.net\/users\/(?<username>\d+)\/?$/,
    example: '11',
  },
]

function stripUrlSuffix(url: string): string {
  return url.replace(/[?#].*$/, '')
}

/**
 * Extracts a username from a raw input value.
 * Handles full URLs, @-prefixed usernames, and plain usernames.
 */
export function sanitizeSocialInput(
  meta: SocialNetworkMeta,
  rawValue: string,
): string {
  const trimmed = rawValue.trim()
  if (!trimmed) return ''

  // Try to extract username from URL (try as-is first, then with query/fragment stripped)
  let urlMatch = trimmed.match(meta.urlExtract)
  if (!urlMatch) {
    urlMatch = stripUrlSuffix(trimmed).match(meta.urlExtract)
  }
  if (urlMatch?.groups?.username) {
    return urlMatch.groups.username
  }

  // Strip leading @
  const stripped = trimmed.replace(/^@/, '')

  // If it matches the username pattern, return it
  if (meta.usernameVerify.test(stripped)) {
    return stripped
  }

  // Return as-is (will fail validation on display)
  return trimmed
}

/**
 * Resolves a stored social network value to a URL.
 * Returns `isSafe: true` if the value is a verified username.
 */
export function resolveSocialUrl(
  meta: SocialNetworkMeta,
  storedValue: string | null | undefined,
): { url: string; isSafe: boolean } | null {
  if (!storedValue) return null

  const trimmed = storedValue.trim()
  if (!trimmed) return null

  // Normalize stored value (strips @, extracts from URL, etc.)
  const sanitized = sanitizeSocialInput(meta, trimmed)

  if (meta.usernameVerify.test(sanitized)) {
    return { url: `${meta.domain}${sanitized}`, isSafe: true }
  }

  // Unverifiable — route through warning
  return { url: trimmed, isSafe: false }
}

/**
 * Returns true if the value is empty or a valid username (after sanitization).
 */
export function isValidSocialInput(
  meta: SocialNetworkMeta,
  rawValue: string,
): boolean {
  const trimmed = rawValue.trim()
  if (!trimmed) return true
  const sanitized = sanitizeSocialInput(meta, trimmed)
  return meta.usernameVerify.test(sanitized)
}

/**
 * Returns a formatted preview URL string for display in the edit form,
 * or null if the input is empty/invalid.
 */
export function formatSocialPreviewUrl(
  meta: SocialNetworkMeta,
  rawValue: string,
): string | null {
  const sanitized = sanitizeSocialInput(meta, rawValue)
  if (!sanitized) return null

  if (meta.usernameVerify.test(sanitized)) {
    return `${meta.domain}${sanitized}`
  }

  return null
}
